import axios from "axios";
import { CHUNK_LOAD_PROGRESS } from "./types";

// Keys per chunk of the Scenario Workspace feeds. The backend pages whole keys, never rows:
// contracts for the roadmap and the usage feed, resources for the supply and workload grids.
export const CONTRACTS_PER_CHUNK = 5;
// The roadmap is heavy per contract (every roadmap row, its parents, tags and structure), so a
// portfolio of a few large contracts would otherwise be one single request: one contract per chunk
// draws the first bars after a fraction of the work, whatever the number of contracts.
export const ROADMAP_CONTRACTS_PER_CHUNK = 1;
export const RESOURCES_PER_CHUNK = 25;

export const chunkLoadProgress = (feed, { loaded, total, complete }) => ({
  type: CHUNK_LOAD_PROGRESS,
  payload: { feed, loaded, total, streaming: !complete },
});

// Every load of a feed starts from a clean slate: a load superseded mid-stream (a period or
// scenario change) never reports again, so its "streaming" flag would otherwise outlive it.
export const startChunkLoad = (feed) => chunkLoadProgress(feed, { loaded: 0, total: 0, complete: true });

// Runs async task factories with a bounded number in flight at a time, preserving order.
// Same helper as the look-ahead grid's (scheduleActivityActions.js).
export const runWithConcurrency = async (taskFactories, limit) => {
  let next = 0;
  const runners = Array.from(
    { length: Math.min(limit, taskFactories.length) },
    async () => {
      while (next < taskFactories.length) {
        const current = next;
        next += 1;
        await taskFactories[current]();
      }
    },
  );
  await Promise.all(runners);
};

// Same pool size as the look-ahead grid: enough to stream, not enough to stampede the backend.
export const CHUNK_CONCURRENCY = 4;

const withChunkParams = (url, chunk, chunkSize) =>
  `${url}${url.includes("?") ? "&" : "?"}chunk=${chunk}&chunkSize=${chunkSize}`;

/**
 * Loads a feed the backend serves as `{ data, meta: { totalChunks, totalRows } }` pages of whole
 * keys (contracts or resources — see the backend doc "Chunked loading of the Scenario Workspace
 * feeds"). Chunk 1 comes first, the rest a few at a time.
 *
 * `onProgress({ rows, loaded, total, complete })` fires after every chunk with every row received
 * so far, in CHUNK-INDEX order rather than arrival order: chunks land out of order, and the
 * backend sorts the keys so that index order is the order of the full response. `loaded` and
 * `total` count keys (projects, resources), which is what the progress chip shows.
 *
 * Resolves with all the rows. A backend that answers a plain array (no chunking) is one complete
 * chunk. Nothing is reported once `signal` is aborted. A failed chunk after the first does not
 * stop the others: the load settles with what arrived, then rejects with that chunk's error.
 */
export const loadInChunks = async ({
  url,
  chunkSize,
  signal,
  onProgress = () => {},
  concurrency = CHUNK_CONCURRENCY,
}) => {
  const first = await axios.get(withChunkParams(url, 1, chunkSize), { signal });
  if (signal?.aborted) return [];

  const body = first.data;
  if (!body || Array.isArray(body) || !body.meta) {
    const rows = Array.isArray(body) ? body : body?.data || [];
    onProgress({ rows, loaded: 0, total: 0, complete: true });
    return rows;
  }

  const totalChunks = body.meta.totalChunks || 0;
  const totalKeys = body.meta.totalRows || 0;
  const keysPerChunk = body.meta.chunkSize || chunkSize;
  const keysOf = (index) => Math.max(0, Math.min(keysPerChunk, totalKeys - (index - 1) * keysPerChunk));
  const slots = new Array(Math.max(totalChunks, 1)).fill(null);
  slots[0] = body.data || [];
  let landed = 1;
  let loadedKeys = keysOf(1);
  const assembled = () => slots.flatMap((slot) => slot || []);
  onProgress({
    rows: assembled(),
    loaded: loadedKeys,
    total: totalKeys,
    complete: totalChunks <= 1,
  });

  let failure = null;
  const fetchChunk = async (index) => {
    if (signal?.aborted) return;
    try {
      const res = await axios.get(withChunkParams(url, index, chunkSize), { signal });
      slots[index - 1] = res.data?.data || [];
    } catch (err) {
      if (signal?.aborted || axios.isCancel(err)) return;
      failure = failure || err;
      slots[index - 1] = [];
    }
    landed += 1;
    loadedKeys += keysOf(index);
    if (signal?.aborted) return;
    onProgress({
      rows: assembled(),
      loaded: loadedKeys,
      total: totalKeys,
      complete: landed === totalChunks,
    });
  };
  const tasks = Array.from({ length: Math.max(totalChunks - 1, 0) }, (_, i) => () => fetchChunk(i + 2));
  await runWithConcurrency(tasks, concurrency);

  if (failure) throw failure;
  return assembled();
};
